/* BIRDLINE — Field Set rendering.
   ------------------------------------------------------------------------
   Everything a customer reads about the ladder is written here, and every
   number it writes comes from window.BL.ladderState(). This file computes no
   prices and contains no price or copy strings: templates come from
   locales/en.default.json via the tier config block.

   Three surfaces, one state object, so the grid and the drawer can never
   disagree about what a tee costs right now:
     · the meter's two lines
     · the Shop All sub-line
     · every collection card's price and in-cart badge
   ------------------------------------------------------------------------ */
(function () {
  var BL = (window.BL = window.BL || {});
  if (!BL.ladderState) return;

  /* ---- the meter ------------------------------------------------------- */

  function paintMeter(state) {
    var copy = BL.meterCopy(state);
    document.querySelectorAll('[data-bl-ladder-big]').forEach(function (el) {
      if (el.textContent !== copy.big) el.textContent = copy.big;
    });
    document.querySelectorAll('[data-bl-ladder-lead]').forEach(function (el) {
      if (el.textContent !== copy.lead) el.textContent = copy.lead;
    });
    document.querySelectorAll('.bl-ship').forEach(function (el) {
      el.classList.toggle('is-fieldset', !!state.setUnlocked);
    });
    /* The extended-size note lives on the Shop All button now, rendered by
       Liquid beside the price line. It needs no painting: the button only
       exists past the set threshold, so the qualifier cannot outlive it. */
  }

  /* ---- Shop All -------------------------------------------------------- */

  /* The button answers "what would I pay after tapping this", so its sub-line
     is priceIfAdded(). At or past the threshold that is the same number as
     priceFor(); below it, it is not — and conflating the two is the most likely
     bug in this build, so they stay named for the question they answer. */
  /* The button has two jobs and one element.

     Below the set threshold it is an invitation — "add one more tee" — and the
     sub-line answers "and then what does a tee cost", which is priceIfAdded().
     At or past the threshold it is the payoff, and priceIfAdded() equals
     priceFor(), so the same call is correct on both sides of the line. That is
     why the sub-line never needed a conditional: the question the button asks
     is always "what would I pay after tapping this". */
  function paintShopAll(state) {
    var show = !!(state.enabled && state.shipUnlocked);
    var live = !!state.setUnlocked;

    var label = live
      ? (BL.strings.shopAll || '')
      : (state.setRemaining === 1
          ? (BL.strings.activateBigOne || '')
          : String(BL.strings.activateBig || '').replace('{{k}}', state.setRemaining));

    document.querySelectorAll('[data-bl-shopall]').forEach(function (el) {
      /* Liquid decides whether this renders at all, and on a normal drawer
         refresh that is enough. This is the belt: a cart that drops below the
         threshold without a re-render would otherwise leave a button offering
         an price the cart no longer qualifies for. */
      el.hidden = !show;
      el.classList.toggle('bl-shopall--activate', show && !live);
      var lab = el.querySelector('[data-bl-shopall-label]');
      if (lab && label && lab.textContent !== label) lab.textContent = label;
    });

    if (!show) return;
    var line = BL.priceline(state, 'ifAdded');
    document.querySelectorAll('[data-bl-priceline]').forEach(function (el) {
      if (el.textContent !== line) el.textContent = line;
    });
  }

  /* ---- collection grid ------------------------------------------------- */

  function inCartHandles(state) {
    var set = {};
    var map = BL.tierMap();
    (map.lines || []).forEach(function (l) { if (l.handle) set[l.handle] = true; });
    if (BL.cart && BL.cart.items) {
      BL.cart.items.forEach(function (i) { if (i.handle) set[i.handle] = true; });
    }
    return set;
  }

  /* ---- prices ---------------------------------------------------------- */

  /* One convention for every price on the storefront.

     Any element that displays a single tee's money carries:
       data-bl-set-price   the FULL amount in cents, written by Liquid
       data-bl-set-units   how many tees that amount covers (default 1)

     That is deliberately the variant's own price and not a tier lookup. A 3XL
     costs more than a small, so pricing off the tier's headline figure would
     quietly hand away the extended-size upcharge. Subtracting the fixed
     reduction from whatever the item actually costs keeps the give identical
     on every unit and lets a mixed-size cart price each line correctly.

     Units is what lets a cart line showing a quantity total use the same rule
     as a shelf price: the reduction is applied once per tee on the line, not
     once for the line.

     PDP price, add-to-cart button, sticky bar, cart lines, cross-sell tiles
     and collection cards all go through here, so none of them can disagree. */

  function writeMoney(el, cents) {
    var next = BL.moneyCents(cents);
    /* Rewrite only the money text node, so a qualifier that lives beside it
       ('From', a bullet, a label span) survives untouched. */
    var money = el.lastChild;
    if (money && money.nodeType === 3) {
      var lead = money.previousSibling ? ' ' : '';
      if (money.nodeValue.trim() !== next) money.nodeValue = lead + next;
    } else if (el.textContent.trim() !== next) {
      el.textContent = next;
    }
  }

  /* ---- preview mode -------------------------------------------------------
     The Field Set invitation sends a shopper to the collection with
     field_set=1. Landing there on full prices makes the button feel like it
     did nothing, so browse surfaces PREVIEW the set price instead.

     Two rules keep this honest:
       - it never touches the cart. The drawer, the cart page and the
         checkout button keep quoting what the shopper owes RIGHT NOW, so
         nothing here can show a total the checkout will not honour.
       - every previewed price carries the count that unlocks it, so $34 is
         never shown bare as though it were already theirs.
     The flag lives in sessionStorage as well as the URL so that clicking
     from a card into the product page does not silently drop back to full
     price mid-decision. It clears itself the moment the set unlocks or the
     cart falls back below the invitation. */
  var PREVIEW_KEY = 'bl:preview';

  /* Only these surfaces preview. The drawer and cart wrappers are absent on
     purpose -- that is the whole safety property. */
  /* The Add to Cart button was removed from this list on 2026-09-10. A button
     that says "Add to cart - $34" while the click charges $39.95 is simply
     wrong, and once the preview fires for everyone holding a tee it would have
     been wrong on most product views. The button states the charge. */
  var PREVIEW_WRAPS = '.bl-card__price, .bl-pdp__price, .bl-sticky__price, .bl-qv__price';

  function previewFlag(state) {
    var stored = false;
    try { stored = sessionStorage.getItem(PREVIEW_KEY) === '1'; } catch (e) {}

    var asked = false;
    try { asked = new URLSearchParams(location.search).get('field_set') === '1'; } catch (e) {}

    /* Unlocked, or no longer holding enough to have been invited: the
       preview has done its job or was never earned. Forget it. */
    if (!state || !state.enabled || state.setUnlocked || !state.tees) {
      try { sessionStorage.removeItem(PREVIEW_KEY); } catch (e) {}
      return false;
    }

    if (asked) {
      try { sessionStorage.setItem(PREVIEW_KEY, '1'); } catch (e) {}
      return true;
    }
    /* Holding a tee is the invitation now. Anyone past the guard above has at
       least one tee in the cart and has not reached the set tier, which is
       exactly the shopper the preview is for -- there is no reason to make them
       have come through the Shop All link to be told what a third tee costs.
       The ?field_set=1 param and the session key above are left in place: they
       are now a subset of this and cost nothing, and the housekeeping in the
       guard still has to run. */
    return true;
  }

  /* "with your 3rd tee" beats "with 1 more" when the number is knowable. */
  function previewNote(state) {
    var k = Number(state && state.setRemaining);
    if (!isFinite(k) || k < 1) k = 1;
    var tpl = k === 1 ? (BL.strings.previewNoteOne || '') : (BL.strings.previewNote || '');
    return String(tpl).replace('{{k}}', k);
  }

  var PRICE_WRAPS = '.bl-card__price, .bl-pdp__price, .bl-drawer__price, .bl-xs__price, .bl-sticky__price, .bl-pdp__atc, .bl-qv__price';

  function paintPrices(state) {
    var on = !!(state && state.enabled && state.setUnlocked);
    /* Called unconditionally: previewFlag does its own housekeeping (it
       forgets the flag once the set is unlocked or the cart empties), and
       short-circuiting past it on `on` would leave a stale flag behind to
       resurface later. */
    var invited = previewFlag(state);
    var preview = !on && invited;
    var note = BL.strings.setPriceNote || '';
    var pNote = preview ? previewNote(state) : '';

    document.querySelectorAll('[data-bl-set-price]').forEach(function (el) {
      var full = parseInt(el.getAttribute('data-bl-set-price'), 10);
      if (!isFinite(full)) return;
      var units = parseInt(el.getAttribute('data-bl-set-units'), 10);
      if (!isFinite(units) || units < 1) units = 1;

      /* Cache the full-price render, keyed on the stamp. When the stamp
         changes the element has just been re-rendered at full price by the
         theme -- a size switch on the PDP, a drawer refresh after a quantity
         tap -- so the cache has to be taken again or dropping below the
         threshold would restore the price of the previous variant. */
      var key = full + ':' + units;
      if (el.dataset.blPriceKey !== key) {
        el.dataset.blPriceFull = el.innerHTML;
        el.dataset.blPriceKey = key;
      }

      /* A previewed element is one the shopper is BROWSING. Anything inside
         the drawer or the cart is excluded by PREVIEW_WRAPS not listing it,
         so those keep quoting the real, current price. */
      var showsSet = on || (preview && (el.matches(PREVIEW_WRAPS) || !!el.closest(PREVIEW_WRAPS)));

      /* Shopify's own number, when there is one.

         Cart-side elements carry data-bl-set-final: the price the CHECKOUT
         will actually charge, rendered by Liquid from item.final_line_price.
         Where that exists we display it verbatim instead of recomputing
         full - reduction.

         This is what makes the storefront correct in any currency. Field Set
         amounts are chosen per market, so the reduction in a CAD cart is not
         the reduction in a USD cart; recomputing here would mean the theme
         and the discount function each doing their own arithmetic and having
         to agree forever. Reading the answer removes that obligation.

         Cards, PDP and Quick View have no cart line and therefore no stamp,
         so they still compute -- which is right, because what they show is a
         PREVIEW of a price that does not exist yet. */
      var finalRaw = el.getAttribute('data-bl-set-final');
      var finalCents = finalRaw === null ? null : parseInt(finalRaw, 10);
      var hasFinal = finalCents !== null && isFinite(finalCents);

      /* A preview is not a price. Until the third tee is actually in the cart
         this shopper pays the full amount, so the price element goes on saying
         the full amount and the Field Set number is carried by the note beside
         it. Only an unlocked cart rewrites money. */
      var previewing = showsSet && !on;

      if (showsSet && !previewing) {
        writeMoney(el, hasFinal ? finalCents : BL.setPriceCents(full, units));
      } else if (el.innerHTML !== el.dataset.blPriceFull) {
        el.innerHTML = el.dataset.blPriceFull;
      }

      el.classList.toggle('is-fieldset', showsSet && !previewing);
      el.classList.toggle('is-fieldset-preview', false);
      var wrap = el.closest(PRICE_WRAPS);
      if (wrap && wrap !== el) {
        wrap.classList.toggle('is-fieldset', showsSet && !previewing);
        wrap.classList.toggle('is-fieldset-preview', previewing);
      }
    });

    document.querySelectorAll('[data-bl-setnote]').forEach(function (el) {
      /* A note next to a drawer or cart price would be describing a price
         that element is not showing, so it follows the same scoping. */
      var scoped = on || (preview && !!el.closest(PREVIEW_WRAPS + ', [data-bl-card]'));
      var copy = on ? note : pNote;

      /* Birdline, Sep 26 2026: a card for a product with no Field Set price
         (the long sleeves) must not say "with your 3rd tee" at all. */
      var cardHost = el.closest('[data-bl-card]');
      if (cardHost && !cardHost.querySelector('[data-bl-set-price]')) {
        el.hidden = true;
        return;
      }

      /* In preview the note is the ONLY place the Field Set number appears, so it
         has to carry the number: "$34.00 with 2 more tees". The amount is read
         from the price element this note sits with, so a $45.95 tee and a $39.95
         tee each quote their own rather than sharing one string. */
      if (!on && copy) {
        var host = el.closest(PREVIEW_WRAPS + ', [data-bl-card]') || el.parentElement;
        var priceEl = host && host.querySelector('[data-bl-set-price]');
        var pCents = priceEl ? parseInt(priceEl.getAttribute('data-bl-set-price'), 10) : NaN;
        var pUnits = priceEl ? parseInt(priceEl.getAttribute('data-bl-set-units'), 10) : NaN;
        if (!isFinite(pUnits) || pUnits < 1) pUnits = 1;
        if (isFinite(pCents)) copy = BL.moneyCents(BL.setPriceCents(pCents, pUnits)) + ' ' + copy;
      }

      if (scoped && copy) {
        if (el.textContent !== copy) el.textContent = copy;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
  }

  /* ---- cart recall ------------------------------------------------------ */

  /* The offer lives in the empty cart and nowhere else: the one screen where
     there is nothing to interrupt and nothing to undercut. BL.recall decides
     whether there is anything worth offering -- it will not offer a cart that
     was checked out or emptied on purpose -- and this only draws it. */
  /* A restore that came back short owes the shopper a reason. The count is
     handed over in a one-shot session flag because the restore navigates to
     the cart page to show the result. */
  var noticeShown = false;
  function noticeOnce() {
    if (noticeShown) return;
    var el = document.querySelector('[data-bl-recall-notice]');
    if (!el) return;
    noticeShown = true;
    var n = 0;
    try {
      n = parseInt(sessionStorage.getItem('bl:recall:missing'), 10) || 0;
      sessionStorage.removeItem('bl:recall:missing');
    } catch (e) {}
    if (!n) return;
    el.textContent = n === 1
      ? 'One tee from last time is no longer available, so it was left out.'
      : n + ' tees from last time are no longer available, so they were left out.';
    el.hidden = false;
  }

  function paintRecall() {
    noticeOnce();
    var boxes = document.querySelectorAll('[data-bl-recall]');
    if (!boxes.length) return;
    var snap = (BL.recall && BL.recall.pending) ? BL.recall.pending() : null;

    boxes.forEach(function (box) {
      if (!snap) { box.hidden = true; return; }

      /* Only rebuild when the saved cart actually changed. */
      if (box.dataset.blRecallTs !== String(snap.ts)) {
        box.dataset.blRecallTs = String(snap.ts);

        var lead = box.querySelector('[data-bl-recall-lead]');
        if (lead) {
          lead.textContent = snap.count === 1
            ? 'You left one tee here last time.'
            : 'You left ' + snap.count + ' tees here last time.';
        }

        var list = box.querySelector('[data-bl-recall-list]');
        if (list) {
          list.textContent = '';
          snap.items.forEach(function (it) {
            var li = document.createElement('li');
            li.className = 'bl-recall__item';
            if (it.img) {
              var img = document.createElement('img');
              img.src = it.img + (it.img.indexOf('?') > -1 ? '&' : '?') + 'width=120';
              img.alt = '';
              img.width = 60;
              img.height = 60;
              img.loading = 'lazy';
              li.appendChild(img);
            }
            var span = document.createElement('span');
            span.textContent = it.title +
              (it.variant ? ' \u2014 ' + it.variant : '') +
              (it.qty > 1 ? ' \u00d7' + it.qty : '');
            li.appendChild(span);
            list.appendChild(li);
          });
        }

        var miss = box.querySelector('[data-bl-recall-miss]');
        if (miss) { miss.textContent = ''; miss.hidden = true; }
      }

      box.hidden = false;
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var go = t.closest('[data-bl-recall-go]');
    if (go) {
      if (go.disabled) return;
      var box = go.closest('[data-bl-recall]');
      var label = go.textContent;
      go.disabled = true;
      go.textContent = 'Putting them back\u2026';
      BL.recall.restore().then(function (res) {
        go.disabled = false;
        go.textContent = label;

        /* Nothing came back -- every line has gone out of stock. Say so where
           the list was, rather than silently doing nothing. */
        if (!res.added) {
          if (box) {
            var gone = box.querySelector('[data-bl-recall-miss]');
            if (gone) {
              gone.textContent = 'Those tees are no longer available.';
              gone.hidden = false;
            }
            var l = box.querySelector('[data-bl-recall-list]');
            if (l) l.textContent = '';
          }
          return;
        }

        /* The cart page renders the restored lines, their Field Set prices and
           the meter in one go, which a re-opened drawer would not. */
        try { sessionStorage.setItem('bl:recall:missing', String(res.missing.length)); } catch (err) {}
        location.assign('/cart');
      });
      return;
    }

    var no = t.closest('[data-bl-recall-dismiss]');
    if (no) {
      if (BL.recall && BL.recall.spend) BL.recall.spend();
      var b = no.closest('[data-bl-recall]');
      if (b) b.hidden = true;
    }
  });

  /* ---- collection grid -------------------------------------------------- */

  /* Only the badge lives here now; the card's price goes through paintPrices
     with everything else. The badge marks products that are ACTUALLY in the
     cart, which is the point of it -- someone browsing back from Shop All
     needs to tell at a glance which designs they already picked. The card is
     never hidden: wanting a second one in another size is a normal thing. */
  function paintCards(state) {
    var cards = document.querySelectorAll('[data-bl-card]');
    if (!cards.length) return;
    var carted = inCartHandles(state);
    cards.forEach(function (card) {
      var badge = card.querySelector('[data-bl-incart]');
      if (badge) badge.hidden = !carted[card.getAttribute('data-bl-handle')];
    });
  }

  /* ---- the promise check ------------------------------------------------ */

  /* The storefront will happily print the Field Set price whether or not the
     automatic discounts exist in Shopify. Printing a price the checkout will
     not charge is the worst thing this build could do, so when the cart
     qualifies and Shopify has applied nothing, say so as loudly as a preview
     allows. Silent on the live storefront -- a shopper is not the audience
     for this -- but impossible to miss while the theme is being set up. */
  var warnedPromise = false;
  function checkPromise(state) {
    if (warnedPromise || !state || !state.setUnlocked) return;
    var map = BL.tierMap();
    if (!map || map.discountApplied !== false) return;
    warnedPromise = true;
    if (BL.dev && window.console && console.error) {
      console.error(
        '[BL ladder] The cart qualifies for Field Set pricing and this page is ' +
        'showing the reduced prices, but Shopify has applied no discount to the ' +
        'cart. Until the automatic Buy X Get Y discounts exist, checkout will ' +
        'charge full price and the storefront is promising something it cannot keep.'
      );
    }
  }

  /* ---- paint ----------------------------------------------------------- */

  function paint(state) {
    var st = state || BL.ladderState();
    if (!st) return;
    /* Whatever we just painted from IS the current state. Without this,
       BL.state keeps whatever the last /cart.js fetch produced, and a drawer
       re-render (which repaints from the fresh DOM map without re-fetching)
       leaves anything reading BL.state — analytics, the Shop All handler —
       describing a cart that no longer exists. */
    BL.state = st;
    paintMeter(st);
    paintShopAll(st);
    paintPrices(st);
    paintCards(st);
    paintRecall();
    checkPromise(st);
  }
  BL.paintLadder = paint;

  /* A collection card add is tracked here rather than in the cart controller,
     because on a grid page the add can come from Quick View, which owns its own
     request and only tells the theme afterwards. */
  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-bl-card]');
    if (!card) return;
    var trigger = e.target.closest('[data-bl-add], [data-bl-quick-add], .bl-card__add');
    if (!trigger) return;
    var st = BL.ladderState();
    if (!st || !st.setUnlocked) return;
    BL.track('collection_fieldset_add', {
      tier: card.getAttribute('data-bl-tier'),
      handle: card.getAttribute('data-bl-handle'),
      tees: st.tees
    });
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-bl-shopall]')) return;
    var st = BL.ladderState();
    BL.track('shopall_click', { tees: st ? st.tees : 0, surface: 'grid' });
  });

  /* Repaint on every signal the cart can give us. field_set=1 in the URL is a
     display hint: it can PREVIEW the set price on browse surfaces, labelled
     with what unlocks it, but it cannot fake the tier. Real state still comes
     from the cart, and the cart is what the drawer, the cart page and the
     checkout button all quote. */
  /* Every one of these recomputes rather than painting a state handed to it.
     That is not defensiveness, it is a bug fix: BL.refresh() resolves with a
     state built when its /cart.js call STARTED, and a drawer refresh that
     lands in between leaves that snapshot describing the previous cart. Painting
     it undoes the correct paint and puts full prices back under a Field Set
     meter. The tier map in the DOM is the freshest thing available at the
     moment of painting, so read it then. */
  /* Map only, deliberately. ladderState(cart) lets a passed cart override the
     map's quantities, which is right when the cart is the fresher of the two
     and wrong when it is not — and BL.cart is exactly the thing that goes stale,
     because it is whatever the last /cart.js call returned. The tier map is
     re-rendered by the server on every cart mutation, so it is never behind. */
  function repaintNow() { paint(BL.ladderState()); }

  document.addEventListener('bl:ladder', repaintNow);
  document.addEventListener('bl:cart:updated', function () { BL.refresh().then(repaintNow); });
  document.addEventListener('cart:refresh', function () { BL.refresh().then(repaintNow); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { paint(); });
  } else {
    paint();
  }

  /* Priced markup arrives after that first paint all the time: infinite scroll,
     a filter applied through the Section Rendering API, Quick View swapping a
     grid in, and — the one that actually bit — the cart drawer re-rendering its
     whole line list on every add. Anything the painter never saw keeps the
     price Liquid rendered, which past the set threshold is the wrong number on
     screen, the one failure this build is least allowed to have.

     So watch for either kind of node and repaint, coalesced into one frame so a
     60-card page does not repaint 60 times. */
  var WATCH = '[data-bl-card], [data-bl-set-price], [data-bl-ladder-big]';
  if (window.MutationObserver) {
    /* Debounced, not coalesced-into-one-frame. The earlier version armed a
       single frame and then ignored every mutation until it fired, which meant
       a grid that arrives in two bursts — the common case for lazy-loaded
       cards — got painted after the first burst and never again, leaving the
       stragglers showing full price under a Field Set meter. Re-arming on
       every batch means the paint always happens after the DOM settles. */
    var timer = null;
    var schedule = function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { timer = null; repaintNow(); }, 60);
    };
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches(WATCH) || n.querySelector(WATCH)) { schedule(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
